import { formatDateTime } from 'utils/date-helpers';
import {
  BasicFormState,
  BasicFormValidators,
  ValidationResult,
  ValidationResults,
} from 'hooks/useForm';
import { isExcemptFromEstimationValidation } from 'constants/AppConstants';
import {
  AssignmentStatusEnum,
  AssignmentTypeEnum,
  StayOverTypeEnum,
} from 'api/model';
import {
  bookedToChangeReasonRequired,
  bookedToChangeReasonRequiresComment,
} from 'constants/BookedToChangeReasons';
import {
  AssignmentField,
  isBookedToChanged,
  isSelfTransportationAssignment,
} from './types';

export const validators: BasicFormValidators<AssignmentField> = {
  [AssignmentField.fromAddress]: (v) =>
    v.trim().length === 0 ? { error: 'Ange från, adress' } : undefined,
  [AssignmentField.fromZip]: (v) =>
    v.trim().length === 0 ? { error: 'Ange från, postkod' } : undefined,
  [AssignmentField.fromCity]: (v) =>
    v.trim().length === 0 ? { error: 'Ange från, stad' } : undefined,
  [AssignmentField.fromCounty]: (v) =>
    v.trim().length === 0 ? { error: 'Ange från, län' } : undefined,

  [AssignmentField.toAddress]: (v) =>
    v.trim().length === 0 ? { error: 'Ange till, adress' } : undefined,
  [AssignmentField.toZip]: (v) =>
    v.trim().length === 0 ? { error: 'Ange till, postkod' } : undefined,
  [AssignmentField.toCity]: (v) =>
    v.trim().length === 0 ? { error: 'Ange till, stad' } : undefined,
  [AssignmentField.toCounty]: (v) =>
    v.trim().length === 0 ? { error: 'Ange till, län' } : undefined,

  [AssignmentField.bookedTo]: (v) =>
    v.trim().length === 0 ? { error: 'Ange Bokad till' } : undefined,

  [AssignmentField.estimatedDuration]: (value, inputName, formState) => {
    // only validate if assignee is set
    if (formState[AssignmentField.assignedTo] === '') {
      return undefined;
    }
    // if this assignment has sibling purchase assignments at same booked to and address with estimated start time and duration, don't validate
    if (
      formState[
        AssignmentField
          .hasMultiPurchaseAssignmentSiblingsWithEstimatedDurationAndStartTime
      ] === 'true'
    ) {
      return undefined;
    }

    // if start and end times are set, but equate to 0 duration, then don't validate
    if (
      formState[AssignmentField.startTime] !== undefined &&
      formState[AssignmentField.endTime] !== undefined &&
      formState[AssignmentField.startTime] !== '' &&
      formState[AssignmentField.endTime] !== ''
    ) {
      const startTime = new Date(formState[AssignmentField.startTime]);
      const endTime = new Date(formState[AssignmentField.endTime]);
      if (startTime.getTime() === endTime.getTime()) {
        return undefined;
      }
    }

    if (
      isExcemptFromEstimationValidation(
        Number(formState[AssignmentField.assignedTo])
      )
    ) {
      return undefined;
    }

    if (
      Number(formState[AssignmentField.assignmentStatusID]) ===
      AssignmentStatusEnum.Complete
    ) {
      return undefined;
    }

    if (formState[AssignmentField.fixedAssignmentTimeInHours]) {
      return undefined;
    }

    // return false if the value is not a number or is less than 0
    return !(Number(value) > 0)
      ? { error: 'Ange estimerad uppdragstid' }
      : undefined;
  },
  [AssignmentField.estimatedStartTime]: (value, inputName, formState) => {
    // only validate if assignee is set
    if (formState[AssignmentField.assignedTo] === '') {
      return undefined;
    }

    // if this is a new assignment and the firstAllowedStartTime is set, then make sure that the estimated start time is after that
    if (
      formState[AssignmentField.isNewAssignment] === 'true' &&
      formState[AssignmentField.firstPossibleStartTime] !== ''
    ) {
      const firstAllowedStartTime = new Date(
        formState[AssignmentField.firstPossibleStartTime]
      );
      const estimatedStartTime = new Date(value);
      if (estimatedStartTime < firstAllowedStartTime) {
        return {
          error: 'Estimerad starttid måste vara efter första möjliga starttid',
        };
      }
    }

    // if this is a new assignment and the lastPossibleEndTime is set, then make sure that the estimated start time + estimated duration is before that
    if (
      formState[AssignmentField.isNewAssignment] === 'true' &&
      formState[AssignmentField.lastPossibleEndTime] !== ''
    ) {
      const lastPossibleEndTime = new Date(
        formState[AssignmentField.lastPossibleEndTime]
      );
      const estimatedStartTime = new Date(value);
      const estimatedDuration =
        Number(formState[AssignmentField.estimatedDuration]) || 0;

      const estimatedEndTime = new Date(
        estimatedStartTime.getTime() + estimatedDuration
      );

      if (estimatedEndTime > lastPossibleEndTime) {
        return {
          error: `Beräknad sluttid (${formatDateTime(estimatedEndTime)}) måste sluta före sista möjliga sluttid (${formatDateTime(lastPossibleEndTime)})`,
        };
      }
    }

    // if this is a new assignment and the fromAddressIsOutSideHomeArea is set and this is the user's first assignment of the day, then warn
    if (
      formState[AssignmentField.isNewAssignment] === 'true' &&
      formState[AssignmentField.fromAddressIsOutSideHomeArea] === 'true' &&
      formState[AssignmentField.isUsersFirstAssignmentOfDay] === 'true'
    ) {
      return {
        warning: 'Från-adressen är utanför hemområdet',
      };
    }

    // if this is a new assignment and the toAddressIsOutSideHomeArea is set and this is the user's last assignment of the day, then warn
    if (
      formState[AssignmentField.isNewAssignment] === 'true' &&
      formState[AssignmentField.toAddressIsOutSideHomeArea] === 'true' &&
      formState[AssignmentField.isUsersLastAssignmentOfDay] === 'true'
    ) {
      return {
        warning: 'Till-adressen är utanför hemområdet',
      };
    }

    // if this assignment has sibling purchase assignments at same booked to and address with estimated start time and duration, don't validate
    if (
      formState[
        AssignmentField
          .hasMultiPurchaseAssignmentSiblingsWithEstimatedDurationAndStartTime
      ] === 'true'
    ) {
      return undefined;
    }
    if (
      isExcemptFromEstimationValidation(
        Number(formState[AssignmentField.assignedTo])
      )
    ) {
      return undefined; // these users don't need to set estimated start time
    }
    if (formState[AssignmentField.startTime] !== '') {
      return undefined; // no need for estimated start time if actual start time is set
    }
    // require if estimated duration is set
    if (
      formState[AssignmentField.estimatedDuration] === undefined ||
      formState[AssignmentField.estimatedDuration] === ''
    ) {
      return undefined;
    }
    return !formState[AssignmentField.estimatedStartTime]
      ? { error: 'Ange estimerad starttid' }
      : undefined;
  },
  [AssignmentField.ticketInfo]: (value, inputName, formState) => {
    // if assignee should NOT book their own ticket and this is a self transport assignment type, require ticket info
    if (
      formState[AssignmentField.assigneeShouldBookTheirOwnTicket] === 'false' &&
      isSelfTransportationAssignment(
        Number(
          formState[AssignmentField.assignmentTypeID]
        ) as AssignmentTypeEnum
      )
    ) {
      return !value ? { error: 'Ange biljettinformation' } : undefined;
    }
    return undefined;
  },
  [AssignmentField.stayOverInfo]: (value, inputName, formState) => {
    // return error if stayOverTypeID is set to HOTEL and stayOverInfo is empty
    if (
      formState[AssignmentField.stayOverTypeID] !== '' &&
      Number(formState[AssignmentField.stayOverTypeID]) ===
        StayOverTypeEnum.HOTEL &&
      (value === '' || value.trim().length === 0)
    ) {
      return { error: 'Ange hotellinformation' };
    }
    return undefined;
  },

  // personal transportation to assignment
  // estimated duration needs to be set if personal transportation is added
  [AssignmentField.personalTransportationToAssignmentEstimatedDuration]: (
    value,
    inputName,
    formState
  ) => {
    if (
      formState[AssignmentField.addingPersonalTransportationToAssignment] ===
      'true'
    ) {
      return !(Number(value) > 0)
        ? { error: 'Ange estimerad tid för personlig transport till uppdrag' }
        : undefined;
    }
    return undefined;
  },
  // estimated start time needs to be set if personal transportation is added, and it + estimated duration must be before actual estimated start time
  [AssignmentField.personalTransportationToAssignmentEstimatedStartTime]: (
    value,
    inputName,
    formState
  ) => {
    const result = {
      error: '',
      warning: '',
    } as ValidationResult;
    if (
      formState[AssignmentField.addingPersonalTransportationToAssignment] ===
      'true'
    ) {
      // if we have a personal transportation to assignment estimated start time and estimated duration, validate that it is before the main assignment's estimated start time
      if (
        formState[AssignmentField.estimatedStartTime] &&
        formState[
          AssignmentField.personalTransportationToAssignmentEstimatedDuration
        ] &&
        formState[
          AssignmentField.personalTransportationToAssignmentEstimatedStartTime
        ]
      ) {
        const mainEstimatedStartTime = new Date(
          formState[AssignmentField.estimatedStartTime]
        );
        const personalTransportationEstimatedStartTime = new Date(value);
        const personalTransportationEstimatedDuration =
          Number(
            formState[
              AssignmentField
                .personalTransportationToAssignmentEstimatedDuration
            ]
          ) || 0;
        const personalTransportationEstimatedEndTime = new Date(
          personalTransportationEstimatedStartTime.getTime() +
            personalTransportationEstimatedDuration
        );
        if (personalTransportationEstimatedEndTime > mainEstimatedStartTime) {
          result.error +=
            'Personlig transport till uppdraget måste sluta före huvuduppdragets starttid';
        }
      }
      // if the estimated start time is not is not set, then warn
      if (
        !formState[
          AssignmentField.personalTransportationToAssignmentEstimatedStartTime
        ]
      ) {
        result.warning +=
          '\nAnge estimerad starttid för personlig transport till uppdrag';
      }

      // if the estimated duration is not set, then warn
      if (
        !formState[
          AssignmentField.personalTransportationToAssignmentEstimatedDuration
        ]
      ) {
        result.warning =
          '\nAnge estimerad tid för personlig transport till uppdrag';
      }
      // if the personal transportation to assignment estimated start time is the day before the main assignment's estimated start time, then warn
      if (
        formState[AssignmentField.estimatedStartTime] &&
        formState[
          AssignmentField.personalTransportationToAssignmentEstimatedStartTime
        ]
      ) {
        const mainEstimatedStartTime = new Date(
          formState[AssignmentField.estimatedStartTime]
        );
        const personalTransportationEstimatedStartTime = new Date(value);
        // check if the personal transportation estimated start time is the day before the main estimated start time
        if (
          personalTransportationEstimatedStartTime.getDate() ===
            mainEstimatedStartTime.getDate() - 1 &&
          personalTransportationEstimatedStartTime.getMonth() ===
            mainEstimatedStartTime.getMonth() &&
          personalTransportationEstimatedStartTime.getFullYear() ===
            mainEstimatedStartTime.getFullYear()
        ) {
          result.warning +=
            '\nPersonlig transport till uppdraget är dagen innan huvuduppdragets starttid';
        }
      }
    }
    return !!result.error || !!result.warning ? result : undefined;
  },
  // personal transportation from assignment
  // estimated start time must not be before the main assignment's estimated end time (start + duration)
  [AssignmentField.personalTransportationFromAssignmentEstimatedStartTime]: (
    value,
    inputName,
    formState
  ) => {
    if (
      formState[AssignmentField.addingPersonalTransportationFromAssignment] !==
      'true'
    ) {
      return undefined;
    }
    if (!value || !formState[AssignmentField.estimatedStartTime]) {
      return undefined;
    }
    const mainEstimatedEndTime = new Date(
      new Date(formState[AssignmentField.estimatedStartTime]).getTime() +
        (Number(formState[AssignmentField.estimatedDuration]) || 0)
    );
    const personalTransportationEstimatedStartTime = new Date(value);
    if (personalTransportationEstimatedStartTime < mainEstimatedEndTime) {
      return {
        error:
          'Personlig transport från uppdraget måste starta efter huvuduppdragets sluttid (starttid + estimerad tid)',
      };
    }
    return undefined;
  },
  [AssignmentField.personalTransportationFromTicketInfo]: (
    value,
    inputName,
    formState
  ) => {
    // if assignee should NOT book their own ticket and addingPersonalTransportationFromAssignment is true, require ticket info
    if (
      formState[
        AssignmentField
          .personalTransportationFromAssigneeShouldBookTheirOwnTicket
      ] === 'false' &&
      formState[AssignmentField.addingPersonalTransportationFromAssignment] ===
        'true'
    ) {
      return !value ? { error: 'Ange biljettinformation' } : undefined;
    }
    return undefined;
  },
  [AssignmentField.personalTransportationToTicketInfo]: (
    value,
    inputName,
    formState
  ) => {
    // if assignee should NOT book their own ticket and addingPersonalTransportationFromAssignment is true, require ticket info
    if (
      formState[
        AssignmentField.personalTransportationToAssigneeShouldBookTheirOwnTicket
      ] === 'false' &&
      formState[AssignmentField.addingPersonalTransportationToAssignment] ===
        'true'
    ) {
      return !value ? { error: 'Ange biljettinformation' } : undefined;
    }
    return undefined;
  },

  // Ny samåkning som planeras (får en fälttestare): bäraren måste väljas, eller "ingen bärare i
  // systemet" med en motivering. Redan planerade samåkningar omfattas inte
  // (ridesAlongRequired = 'false'), se CaseLogic.ApplyRideAlongLink.
  [AssignmentField.ridesAlongWithAssignmentID]: (value, _, formState) => {
    if (
      formState[AssignmentField.ridesAlongRequired] !== 'true' ||
      formState[AssignmentField.assignedTo] === ''
    ) {
      return undefined;
    }
    if (value === '') {
      return { error: 'Välj vilket uppdrag fälttestaren samåker med' };
    }
    if (
      value === 'none' &&
      !formState[AssignmentField.ridesAlongExemptionReason]?.trim()
    ) {
      return { error: 'Ange varför bilen inte finns som uppdrag i systemet' };
    }
    return undefined;
  },

  // Ledningsrapporten räknar ombokningar per orsak, så ett flyttat huvuduppdrag måste ha en.
  [AssignmentField.bookedToChangeReason]: (value, _, formState) => {
    // Fälten döljs för andra typer och värdena skickas inte med, så inget att validera.
    if (
      !isBookedToChanged(formState) ||
      !bookedToChangeReasonRequired(formState[AssignmentField.assignmentTypeID])
    ) {
      return undefined;
    }
    if (value === '') {
      return { error: 'Välj orsak till ny bokad tid' };
    }
    if (
      bookedToChangeReasonRequiresComment(value) &&
      !formState[AssignmentField.bookedToChangeComment]?.trim()
    ) {
      return {
        error: 'Skriv en kommentar när orsaken till ny bokad tid är Annat',
      };
    }
    return undefined;
  },
};

export function createValidations(
  assignmentFormState: BasicFormState<AssignmentField>
) {
  const assignmentValidations: ValidationResults<AssignmentField> = {};

  // do validations
  if (
    assignmentFormState[AssignmentField.assignmentStatusID] ===
    AssignmentStatusEnum.Discarded.toString()
  ) {
    return {}; // don't validate removed assignments
  }
  Object.entries(validators).forEach(([fieldNameStr, validator]) => {
    if (validator && fieldNameStr in assignmentFormState) {
      const fieldName = Number(fieldNameStr) as AssignmentField;

      const v = validator(
        assignmentFormState[fieldName],
        fieldName,
        assignmentFormState
      );
      if (v) assignmentValidations[fieldName] = v;
    }
  });

  return assignmentValidations;
}
